/**
 * NotificationTemplatePage — 알림 템플릿 관리 (W19-C5, SC3 Split + CLOB body 편집).
 *
 * 【역할】
 * - tb_notification_template 마스터 관리 — (templateCode, locale, channel) 3-tuple PK.
 * - 상세 편집: subject 한 줄 + body CLOB TextArea.
 *
 * 【주요 기능】
 * - 좌측: 템플릿 리스트 (keyword/channel/locale 필터)
 * - 우측: 선택 행 편집 (body 는 전용 TextArea) → 변경 시 U state 마킹
 * - 상단 토글 버튼: 신규 / 저장 / 삭제
 *
 * 【사용 훅】
 * - useFramework ($createDataSet, $transaction, $msg)
 * - useLabel (다국어)
 * - useI18nTickStore (locale 전환 re-render)
 *
 * 【Backend RPC serviceName】
 * - notification_template/searchList (search)
 * - notification_template/save (save, C/U/D)
 *
 * 【DataSet 구조】
 * - ds_template: 템플릿 목록 (변경추적 C/U/D, PK=3-tuple)
 *
 * 【사용처】 router/index.tsx — `{ path: 'notification-template' }`
 * 【포팅 원본】 (없음 — Wave 15 백엔드 있었으나 프론트 미구현. Wave 19 에서 이식.)
 */
import { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Flex, Form, Input, Modal, Pagination, Select, Space, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useFramework } from '@/framework/useFramework'
import { useLabel } from '@/framework/useLabel'
import { useI18nTickStore } from '@/framework/useLocale'
import { useSupportedLocales } from '@/framework/useLocale'
import RowStateTag from '@/components/common/RowStateTag'
import AppPage from '@/components/common/AppPage'
import DataTable from '@/components/common/DataTable'

interface TemplateRow {
  /** 템플릿 코드 — PK part1 (예: ORDER_CONFIRM, PASSWORD_RESET) */
  templateCode?: string
  /** 로케일 — PK part2 (ko/en/ja/zh-CN ...) */
  locale?: string
  /** 채널 — PK part3 (EMAIL/SMS/PUSH/SLACK) */
  channel?: string
  /** 제목 — 짧은 한 줄 (이메일 subject 등). 변경 시 onBlur → updateRow */
  subject?: string
  /** 본문 — Oracle CLOB. TextArea 14행. 변경 시 onBlur → updateRow */
  body?: string
  /** 사용여부 — 1=사용, 0=미사용 */
  isActive?: number
}

/** 지원 채널 목록 — Select option 생성에 사용. */
const CHANNELS = ['EMAIL', 'SMS', 'PUSH', 'SLACK'] as const

/**
 * NotificationTemplatePage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function NotificationTemplatePage() {
  const { $createDataSet, $transaction, $msg } = useFramework()
  const { t } = useLabel()
  useI18nTickStore((s) => s.tick)
  const locales = useSupportedLocales()

  const ds_template = $createDataSet<TemplateRow>([])
  const [searchForm] = Form.useForm()
  // 외부 Pagination — DataTable 내부 pagination 대신 Flex column 마지막 자리에 분리 배치
  const TEMPLATE_PAGE_SIZE = 20
  const [templatePage, setTemplatePage] = useState(1)

  const [createOpen, setCreateOpen] = useState(false)
  const [createForm] = Form.useForm<TemplateRow>()
  const [selectedKey, setSelectedKey] = useState<React.Key | null>(null)
  const [loading, setLoading] = useState(false)
  const [bodyText, setBodyText] = useState('')
  const [subjectText, setSubjectText] = useState('')

  /** 검색 — 검색 조건으로 템플릿 목록 조회 + 상세 패널 reset. */
  const fn_search = async () => {
    // ─── 검색 + 상세 패널 reset ─
    // 1) 로딩 표시
    setLoading(true)
    // 2) RPC — inds.ds_search 로 검색조건 전달, outds.ds_template 으로 결과 바인딩
    const res = await $transaction({
      id: 'nt-search',
      service: 'notification_template/searchList',
      action: 'search',
      inds: { ds_search: searchForm.getFieldsValue() },
      outds: { ds_template }
    })
    setLoading(false)
    // 3) 성공 시 상세 패널 (선택 + 편집 버퍼) 모두 reset
    if (res.success) {
      setSelectedKey(null)
      setBodyText('')
      setSubjectText('')
    }
  }

  /** 저장 — 변경분(ds_template)을 일괄 저장 후 재조회 (Redis Pub/Sub 캐시 invalidate). */
  const fn_save = async () => {
    // ─── dirty 체크 + 벌크 저장 ─
    if (!ds_template.isDirty) return $msg.byId('MSG_NO_CHANGES')
    // outds 미사용 — 저장 후 fn_search 로 신선한 데이터 재조회
    const res = await $transaction({
      id: 'nt-save',
      service: 'notification_template/save',
      action: 'save',
      inds: { ds_template }
    })
    if (res.success) {
      $msg.byId('MSG_SAVE_SUCCESS')
      fn_search()
    }
  }

  /** 신규 — Modal 폼 reset + 기본값(EMAIL/ko/사용) 주입 후 open. */
  const fn_create = () => {
    // ─── 신규 Modal 진입 ─
    createForm.resetFields()
    // 합리적 기본값 주입 (가장 자주 쓰는 채널/로케일)
    createForm.setFieldsValue({ isActive: 1, locale: 'ko', channel: 'EMAIL' })
    setCreateOpen(true)
  }

  /** 신규 Modal OK — 폼 검증 후 ds_template 에 addRow. */
  const fn_createSubmit = async () => {
    // ─── 폼 검증 + addRow ─
    const v = await createForm.validateFields()
    // _state='C' 마킹 — 저장 시점 백엔드로 전송
    ds_template.addRow(v)
    setCreateOpen(false)
  }

  /** 삭제 — 선택된 단일 행을 deleteRow + 상세 패널 reset (확인 다이얼로그). */
  const fn_delete = () => {
    // ─── 선택 검증 + confirm + 단일 삭제 ─
    if (selectedKey === null) return $msg.byId('MSG_SELECT_ROW')
    const idx = ds_template.rows.findIndex((r) => r._rowId === selectedKey)
    if (idx < 0) return
    $msg.confirmById('MSG_CONFIRM_DELETE', () => {
      // C/U 행은 'D' 마킹, N 행은 즉시 제거
      ds_template.deleteRow(idx)
      // 상세 패널도 reset (삭제된 행을 계속 표시하지 않도록)
      setSelectedKey(null)
      setBodyText('')
      setSubjectText('')
    })
  }

  useEffect(() => {
    // ─── 초기 로드 (mount 1회) ─
    // 라이프사이클: mount → fn_search → ds_template 채움 → 좌측 그리드 렌더
    fn_search()
  }, []) // deps 비움 — 1회만 실행

  /** 현재 선택 행의 actual 인덱스 — selectedKey 변경 시에만 재계산. */
  const currentIdx = useMemo(() => {
    // ─── selectedKey → ds_template.rows 인덱스 매핑 ─
    if (selectedKey === null) return -1
    return ds_template.rows.findIndex((r) => r._rowId === selectedKey)
  }, [selectedKey, ds_template.rows])

  const currentRow = currentIdx >= 0 ? ds_template.rows[currentIdx] : null

  /** 행 선택 핸들러 — 선택 키 set + 편집 버퍼(subject/body) 동기화. */
  const onSelectRow = (key: React.Key | null) => {
    // ─── 선택 키 + 편집 버퍼 동기화 ─
    setSelectedKey(key)
    const idx = key === null ? -1 : ds_template.rows.findIndex((r) => r._rowId === key)
    if (idx >= 0) {
      // 편집 버퍼에 현재 행 값 로드 (TextArea/Input controlled)
      setBodyText(ds_template.rows[idx].data?.body ?? '')
      setSubjectText(ds_template.rows[idx].data?.subject ?? '')
    } else {
      // 선택 해제 시 버퍼도 비움
      setBodyText('')
      setSubjectText('')
    }
  }

  /** subject onBlur 커밋 — 편집 버퍼 → ds 반영 ('U' 마킹). */
  const commitSubject = () => {
    // ─── subject 편집 → ds 반영 ─
    if (currentIdx < 0) return
    // updateRow → _state='C' 유지, 'N' 이면 'U' 로 전이
    ds_template.updateRow(currentIdx, { subject: subjectText } as TemplateRow)
  }

  /** body onBlur 커밋 — 편집 버퍼 → ds 반영 (CLOB 변경). */
  const commitBody = () => {
    // ─── body(CLOB) 편집 → ds 반영 ─
    if (currentIdx < 0) return
    ds_template.updateRow(currentIdx, { body: bodyText } as TemplateRow)
  }

  const visibleRows = ds_template.getVisibleRows()
  /** 페이지 슬라이스 — 외부 Pagination 의 current 에 맞춰 visibleRows 잘라냄. */
  const pagedRows = useMemo(
    // ─── 페이지 윈도우 = [(page-1)*size, page*size) ─
    () =>
      visibleRows.slice((templatePage - 1) * TEMPLATE_PAGE_SIZE, templatePage * TEMPLATE_PAGE_SIZE),
    [visibleRows, templatePage]
  )
  const columns: ColumnsType<(typeof visibleRows)[number]> = [
    {
      title: t('COL_TEMPLATE_CODE', '템플릿코드'),
      dataIndex: ['data', 'templateCode'],
      width: 180
    },
    { title: t('COL_LOCALE', '로케일'), dataIndex: ['data', 'locale'], width: 80 },
    {
      title: t('COL_CHANNEL', '채널'),
      dataIndex: ['data', 'channel'],
      width: 100,
      render: (c: string) => <Tag color="blue">{c}</Tag>
    },
    { title: t('COL_SUBJECT', '제목'), dataIndex: ['data', 'subject'] },
    {
      title: t('COL_IS_ACTIVE', '사용여부'),
      dataIndex: ['data', 'isActive'],
      width: 90,
      render: (v) =>
        v === 1 ? (
          <Tag color="green">{t('LBL_YES', '사용')}</Tag>
        ) : (
          <Tag>{t('LBL_NO', '미사용')}</Tag>
        )
    },
    {
      title: t('COL_STATUS', '상태'),
      dataIndex: '_state',
      width: 70,
      render: (s: string) => <RowStateTag state={s} />
    }
  ]

  const localeOptions = locales.map((l) => ({
    label: `${l.flagEmoji ?? ''} ${l.locale}`,
    value: l.locale
  }))
  const channelOptions = CHANNELS.map((c) => ({ label: c, value: c }))

  return (
    <>
      <Alert
        type="info"
        showIcon
        message={t(
          'MSG_NOTIFY_TEMPLATE_INFO',
          '알림 템플릿 — (templateCode, locale, channel) 3-tuple PK. 저장 시 Redis Pub/Sub 로 전 인스턴스 캐시 invalidate.'
        )}
        style={{ marginBottom: 12 }}
      />
      <AppPage
        search={{
          form: searchForm,
          onSearch: fn_search,
          loading,
          content: (
            <>
              <Form.Item name="keyword" label={t('LBL_KEYWORD', '키워드')}>
                <Input style={{ width: 200 }} placeholder="templateCode/subject" />
              </Form.Item>
              <Form.Item name="channel" label={t('LBL_CHANNEL', '채널')}>
                <Select allowClear style={{ width: 120 }} options={channelOptions} />
              </Form.Item>
              <Form.Item name="locale" label={t('LBL_LOCALE', '로케일')}>
                <Select allowClear style={{ width: 120 }} options={localeOptions} />
              </Form.Item>
            </>
          )
        }}
        toolbar={
          <Space>
            <Button onClick={fn_search} loading={loading}>
              {t('BTN_SEARCH', '조회')}
            </Button>
            <Button type="primary" onClick={fn_create}>
              {t('BTN_ADD', '추가')}
            </Button>
            <Button onClick={fn_delete} danger disabled={selectedKey === null}>
              {t('BTN_DELETE', '삭제')}
            </Button>
            <Button type="primary" onClick={fn_save} disabled={!ds_template.isDirty}>
              {t('BTN_SAVE', '저장')}
            </Button>
          </Space>
        }
      >
        <Flex gap={16} align="stretch" style={{ flex: 1, minHeight: 0 }}>
          {/* 좌측: 템플릿 목록 — Card 제거, 헤더(h3) + DataTable(pagination=false) + 외부 Pagination */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              gap: 8
            }}
          >
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, flexShrink: 0 }}>
              {t('TTL_TEMPLATE_LIST', '템플릿 목록')}
            </h3>
            <DataTable
              rowKey={(r) => r._rowId}
              columns={columns}
              dataSource={pagedRows}
              loading={loading}
              autoHeight={false}
              pagination={false}
              rowSelection={{
                type: 'radio',
                selectedRowKeys: selectedKey ? [selectedKey] : [],
                onChange: (keys) => onSelectRow(keys[0] ?? null)
              }}
              // 행 셀 클릭만으로도 선택 전환 (라디오 버튼 전용 UX 개선)
              onRow={(row) => ({ onClick: () => onSelectRow((row as { _rowId: string })._rowId) })}
              rowClassName={(row) =>
                (row as { _rowId: string })._rowId === selectedKey ? 'ant-table-row-selected' : ''
              }
            />
            <Pagination
              size="small"
              current={templatePage}
              onChange={setTemplatePage}
              total={visibleRows.length}
              pageSize={TEMPLATE_PAGE_SIZE}
              showSizeChanger={false}
              style={{ flexShrink: 0, textAlign: 'right', marginTop: 4 }}
            />
          </div>

          {/* 우측: 상세 편집 — 좌측 div 와 동일한 플랫 구조 */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '50%',
              minHeight: 0,
              gap: 8,
              overflow: 'auto'
            }}
          >
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, flexShrink: 0 }}>
              {t('TTL_TEMPLATE_DETAIL', '상세 편집')}
            </h3>
            {currentRow ? (
              <>
                <Tag style={{ marginBottom: 8 }}>
                  {currentRow.data?.templateCode} / {currentRow.data?.locale} /{' '}
                  {currentRow.data?.channel}
                </Tag>
                <div style={{ marginBottom: 8 }}>
                  <div style={{ marginBottom: 4, color: '#555' }}>{t('LBL_SUBJECT', '제목')}</div>
                  <Input
                    value={subjectText}
                    onChange={(e) => setSubjectText(e.target.value)}
                    onBlur={commitSubject}
                  />
                </div>
                <div>
                  <div style={{ marginBottom: 4, color: '#555' }}>
                    {t('LBL_BODY', '본문 (CLOB)')}
                  </div>
                  <Input.TextArea
                    rows={14}
                    value={bodyText}
                    onChange={(e) => setBodyText(e.target.value)}
                    onBlur={commitBody}
                  />
                </div>
              </>
            ) : (
              <span style={{ color: '#888' }}>
                {t('MSG_SELECT_TEMPLATE', '좌측에서 템플릿을 선택하세요')}
              </span>
            )}
          </div>
        </Flex>
      </AppPage>

      <Modal
        open={createOpen}
        title={t('TTL_NEW_TEMPLATE', '신규 템플릿')}
        onOk={fn_createSubmit}
        onCancel={() => setCreateOpen(false)}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="templateCode"
            label={t('LBL_TEMPLATE_CODE', '템플릿코드')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="locale" label={t('LBL_LOCALE', '로케일')} rules={[{ required: true }]}>
            <Select options={localeOptions} />
          </Form.Item>
          <Form.Item name="channel" label={t('LBL_CHANNEL', '채널')} rules={[{ required: true }]}>
            <Select options={channelOptions} />
          </Form.Item>
          <Form.Item name="subject" label={t('LBL_SUBJECT', '제목')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="body" label={t('LBL_BODY', '본문')}>
            <Input.TextArea rows={6} />
          </Form.Item>
          <Form.Item name="isActive" label={t('LBL_IS_ACTIVE', '사용여부')}>
            <Select
              options={[
                { label: t('LBL_YES', '사용'), value: 1 },
                { label: t('LBL_NO', '미사용'), value: 0 }
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
